// src/pages/ingresos/components/SerialTagAutocomplete.jsx
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../../../utils/supabase";

/** Pequeño hook para “debounce” del input */
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Autocomplete de instrumentos para un cliente:
 * - Filtra por clientId (obligatorio)
 * - Busca por:
 *   - tag (ILIKE)
 *   - serial (ILIKE)
 *   - brand / model (ILIKE)
 *   - lab_id (numérico exacto)
 *   - qr_scan_value (texto exacto del QR)
 *
 * Props:
 *  - clientId: uuid del cliente (requerido)
 *  - initialQuery?: string
 *  - onPick?: (instrument) => void
 *  - onCreateNew?: (query) => void
 *  - placeholder?: string
 */
export default function SerialTagAutocomplete({
  clientId,
  initialQuery = "",
  onPick,
  onCreateNew,
  placeholder = "Serie / Tag / ID lab…",
}) {
  const [query, setQuery] = useState(initialQuery);
  const debounced = useDebouncedValue(query, 300);
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const listRef = useRef(null);

  // Mantener sincronizado initialQuery -> input (por si algún formulario lo usa)
  useEffect(() => {
    setQuery(initialQuery || "");
  }, [initialQuery]);

  // Cerrar el dropdown al click afuera
  useEffect(() => {
    function onDocClick(e) {
      if (!listRef.current) return;
      if (!listRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Búsqueda directa en instruments (sin RPC), incluyendo lab_id y qr_scan_value
  useEffect(() => {
    let active = true;

    async function search() {
      setErrorMsg("");

      const s = (debounced || "").trim();
      if (!clientId || s.length < 1) {
        if (active) {
          setOpts([]);
          setOpen(false);
        }
        return;
      }

      try {
        setLoading(true);

        const isNum = /^\d+$/.test(s);
        const safe = s.replace(/,/g, " ");

        let queryBuilder = supabase
          .from("instruments")
          .select(
            `
            id,
            lab_id,
            tag,
            serial,
            brand,
            model,
            type,
            category_id,
            subcategory_id,
            unit,
            range_min,
            range_max,
            resolution,
            tolerance,
            is_analog,
            baker_code,
            qr_scan_value,
            reception_date,
            calibration_place,
            departure_date
          `
          )
          .eq("client_id", clientId)
          .order("lab_id", { ascending: true })
          .limit(30);

        const orParts = [
          `tag.ilike.%${safe}%`,
          `serial.ilike.%${safe}%`,
          `brand.ilike.%${safe}%`,
          `model.ilike.%${safe}%`,
        ];

        if (isNum) {
          // Si es numérico, probamos lab_id y qr_scan_value
          orParts.push(`lab_id.eq.${Number(safe)}`);
          orParts.push(`qr_scan_value.eq.${safe}`);
        }

        queryBuilder = queryBuilder.or(orParts.join(","));

        const { data, error } = await queryBuilder;
        if (!active) return;

        if (error) throw error;

        setOpts(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch (err) {
        console.error("[SerialTagAutocomplete] search error:", err);
        if (active) {
          setErrorMsg(err.message || "No se pudo buscar instrumentos.");
          setOpts([]);
          setOpen(false);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    search();
    return () => {
      active = false;
    };
  }, [clientId, debounced]);

  const hasResults = (opts || []).length > 0;
  const canCreate =
    !!clientId && (query || "").trim().length >= 2 && !loading && !hasResults;

  const labelFor = (o) => {
    const lab = o.lab_id != null ? String(o.lab_id).padStart(7, "0") : "—";
    const serialValue = String(o.serial || "").trim() || "sin informar";
    const tagValue = String(o.tag || "").trim() || "sin informar";
    const main = `Serie: ${serialValue} · TAG: ${tagValue}`;
    const bm = [o.brand, o.model].filter(Boolean).join(" ");
    return `#${lab} · ${main}${bm ? ` · ${bm}` : ""}`;
  };

  // Cuando el usuario elige una opción, enviamos el instrumento completo
  const handlePickOption = async (o) => {
    setOpen(false);
    const serialValue = String(o.serial || "").trim() || "sin informar";
    const tagValue = String(o.tag || "").trim() || "sin informar";
    setQuery(`Serie: ${serialValue} · TAG: ${tagValue}`);

    if (!onPick) return;
    try {
      onPick(o); // ya tenemos todos los campos seleccionados arriba
    } catch (err) {
      console.error(
        "[SerialTagAutocomplete] error al devolver instrumento:",
        err
      );
      onPick(o);
    }
  };

  return (
    <div className="relative" ref={listRef}>
      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => hasResults && setOpen(true)}
        placeholder={placeholder}
        disabled={!clientId}
        className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/10 disabled:opacity-50"
      />

      {errorMsg && (
        <p className="mt-1 text-xs text-red-400">{errorMsg}</p>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-md border border-white/10 bg-[#0F1115]">
          {loading && (
            <div className="px-3 py-2 text-sm opacity-70">Buscando…</div>
          )}
          {!loading &&
            hasResults &&
            (opts || []).map((o) => (
              <button
                key={o.id}
                className="block w-full text-left px-3 py-2 hover:bg-white/10 text-sm"
                onClick={() => handlePickOption(o)}
              >
                {labelFor(o)}
              </button>
            ))}
          {!loading && !hasResults && (
            <div className="px-3 py-2 text-sm opacity-70">Sin resultados</div>
          )}
          {canCreate && (
            <button
              className="block w-full text-left px-3 py-2 hover:bg-white/10 text-sm text-emerald-300"
              onClick={() => {
                setOpen(false);
                onCreateNew?.(query.trim());
              }}
            >
              + Crear instrumento con “{query.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
